import { useEffect, useRef, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { useUnsavedStore, type UnsavedEntry } from '../store/unsaved.js';
import { toast } from '../store/toast.js';

// Popup « Modifications non enregistrées ».
//
// Trois issues, jamais plus : Enregistrer puis continuer (par défaut,
// seulement si chaque brouillon sait s'enregistrer), Abandonner (rouge,
// jamais le focus initial — Entrée ne doit pas jeter des données) et
// Rester (Échap). Un enregistrement qui échoue laisse tout en place.

interface Props {
  target: string;
  entries: UnsavedEntry[];
  saving: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onStay: () => void;
}

export function UnsavedChangesModal({ target, entries, saving, onSave, onDiscard, onStay }: Props) {
  const saveRef = useRef<HTMLButtonElement>(null);
  const stayRef = useRef<HTMLButtonElement>(null);
  const canSave = entries.length > 0 && entries.every((e) => !!e.save);

  useEffect(() => {
    (canSave ? saveRef.current : stayRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onStay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canSave, onStay, saving]);

  const many = entries.length > 1;

  return (
    <div className="modal-backdrop" role="presentation" onClick={saving ? undefined : onStay}>
      <div
        className="modal-card unsaved-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-title"
        onClick={(e) => e.stopPropagation()}
        data-testid="unsaved-modal"
      >
        <div className="unsaved-head">
          <span className="unsaved-badge" aria-hidden="true"><TriangleAlert size={20} /></span>
          <div>
            <h2 id="unsaved-title">Modifications non enregistrées</h2>
            <p className="unsaved-sub">
              Vous passez à <b>{target}</b>. Que faire {many ? 'des saisies' : 'de la saisie'} en cours ?
            </p>
          </div>
        </div>
        <ul className="unsaved-list">
          {entries.map((e) => (
            <li key={e.id} data-testid={`unsaved-entry-${e.id}`}>
              <span>{e.label}</span>
              {e.summary && <span className="unsaved-sum">{e.summary}</span>}
            </li>
          ))}
        </ul>
        <div className="modal-actions unsaved-actions">
          <button
            type="button"
            className="btn unsaved-discard"
            onClick={onDiscard}
            disabled={saving}
            data-testid="unsaved-discard"
          >
            Abandonner les modifications
          </button>
          <button type="button" className="btn" ref={stayRef} onClick={onStay} disabled={saving} data-testid="unsaved-stay">
            Rester
          </button>
          {canSave && (
            <button
              type="button"
              className="btn btn--primary"
              ref={saveRef}
              onClick={onSave}
              disabled={saving}
              data-testid="unsaved-save"
            >
              {saving ? <><span className="unsaved-spin" aria-hidden="true" />Enregistrement…</> : 'Enregistrer puis continuer'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Rendu une fois dans AppShell : écoute les demandes du registre
 * (confirmUnsaved) et les résout selon le choix de l'utilisateur.
 */
export function UnsavedChangesHost() {
  const request = useUnsavedStore((s) => s.request);
  const entries = useUnsavedStore((s) => s.entries);
  const setRequest = useUnsavedStore((s) => s.setRequest);
  const [saving, setSaving] = useState(false);
  const list = Object.values(entries);

  // Le brouillon a été résolu ailleurs pendant que la popup était ouverte
  // (enregistré par son propre bouton, par exemple) : plus rien à demander.
  useEffect(() => {
    if (request && list.length === 0) {
      setRequest(null);
      request.resolve(true);
    }
  }, [request, list.length, setRequest]);

  if (!request || list.length === 0) return null;

  const finish = (proceed: boolean) => {
    setSaving(false);
    setRequest(null);
    request.resolve(proceed);
  };

  const onSave = async () => {
    setSaving(true);
    for (const e of list) {
      try {
        await e.save?.();
      } catch {
        toast.danger(`Enregistrement impossible : ${e.label}. Vos modifications sont conservées.`);
        finish(false);
        return;
      }
    }
    finish(true);
  };

  const onDiscard = () => {
    for (const e of list) e.discard?.();
    finish(true);
  };

  return (
    <UnsavedChangesModal
      target={request.target}
      entries={list}
      saving={saving}
      onSave={onSave}
      onDiscard={onDiscard}
      onStay={() => finish(false)}
    />
  );
}
